import { useState, useRef } from 'react';
import type { ItemImage } from '@siliconvault/shared';
import { Button } from '../ui/Button';
import { useTranslation } from '../../i18n';
import styles from './ImageGallery.module.css';

interface ImageGalleryProps {
  itemId: string;
  images: ItemImage[];
  canEdit: boolean;
  onZoom: (imageUrl: string) => void;
  onUpload: (files: File[]) => void;
  onDelete: (imageId: string) => void;
  onSetPrimary: (imageId: string) => void;
  onEditImage: (imageUrl: string, imageId: string) => void;
  isUploading?: boolean;
  aiConfigured?: boolean;
  onReanalyze?: () => void;
  isReanalyzing?: boolean;
}

export function ImageGallery({
  images,
  canEdit,
  onZoom,
  onUpload,
  onDelete,
  onSetPrimary,
  onEditImage,
  isUploading,
  aiConfigured,
  onReanalyze,
  isReanalyzing,
}: ImageGalleryProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const primaryImage = images.find((img) => img.isPrimary) ?? images[0];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = images.find((img) => img.id === selectedId) ?? primaryImage;

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    onUpload(Array.from(files));
    e.target.value = '';
  }

  if (images.length === 0) {
    return (
      <div className={styles.gallery}>
        <div className={styles.placeholder}>
          <span>{t('imageGallery.noImages')}</span>
          {canEdit && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <Button
                size="small"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                loading={isUploading}
              >
                {t('imageGallery.addImage')}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.gallery}>
      {/* Main image */}
      <div
        className={styles.mainImage}
        onClick={() => selected && onZoom(selected.imageUrl)}
      >
        {selected && (
          <img src={selected.imageUrl} alt="" />
        )}
      </div>

      {/* Thumbnail strip (show when more than 1 image) */}
      {images.length > 1 && (
        <div className={styles.thumbnailStrip}>
          {images.map((img) => (
            <div
              key={img.id}
              className={`${styles.thumbnail} ${
                img.id === (selected?.id) ? styles.thumbnailActive : ''
              }`}
              onClick={() => setSelectedId(img.id)}
            >
              <img src={img.imageUrl} alt="" />
              {img.isPrimary && (
                <svg className={styles.primaryBadge} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              )}
              {canEdit && (
                <div className={styles.thumbnailOverlay}>
                  {!img.isPrimary && (
                    <button
                      className={styles.overlayBtn}
                      onClick={(e) => { e.stopPropagation(); onSetPrimary(img.id); }}
                      title={t('imageGallery.setPrimary')}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </button>
                  )}
                  <button
                    className={styles.overlayBtn}
                    onClick={(e) => { e.stopPropagation(); onDelete(img.id); }}
                    title={t('imageGallery.deleteImage')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      {canEdit && (
        <div className={styles.controls}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <Button
            size="small"
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            loading={isUploading}
          >
            {t('imageGallery.addImage')}
          </Button>
          {selected && (
            <Button
              size="small"
              variant="secondary"
              onClick={() => onEditImage(selected.imageUrl, selected.id)}
            >
              {t('itemDetail.editImage')}
            </Button>
          )}
          {selected && aiConfigured && onReanalyze && (
            <Button
              size="small"
              variant="secondary"
              onClick={onReanalyze}
              loading={isReanalyzing}
              disabled={isReanalyzing}
              style={{ marginLeft: 'auto' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: '0.35rem', verticalAlign: 'middle', display: 'inline' }}>
                <path d="M9.5 2l.5 4 4 .5-4 .5-.5 4-.5-4-4-.5 4-.5z" fill="currentColor" />
                <path d="M19 8l.5 3 3 .5-3 .5-.5 3-.5-3-3-.5 3-.5z" fill="currentColor" />
                <path d="M11 15l.5 3 3 .5-3 .5-.5 3-.5-3-3-.5 3-.5z" fill="currentColor" />
              </svg>
              AI {t('itemDetail.reanalyze')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
